import { UiState } from "instantsearch.js"
import { history } from "instantsearch.js/es/lib/routers"
import { RouterProps } from "instantsearch.js/es/middlewares"
import { useMemo } from "react"
import { Configure, InstantSearch } from "react-instantsearch"

import { DocumentData } from "../../utils/types"
import { createTypesenseSearchClient } from "../../utils/typesense"
import CardGrid from "./card-grid"
import ClearFilters from "./clear-filters"
import List from "./list"
import MenuSelect from "./menu-select"
import Pagination from "./pagination"
import SearchBox from "./search-box"

export type EntityIndexProps = {
  entity: string
  collection: string
  clientKey: string
  filters: {
    name: string
    field: string
    labels?: Record<string, string>
  }[]
  refinements?: {
    facet: string
    value: string
    operator: string
  }[]
}

export type RouteState = {
  q?: string
} & Partial<Record<string, string>>

export default function IndexSearch({
  entity,
  clientKey,
  collection,
  filters,
  refinements,
}: EntityIndexProps) {
  const searchClient = useMemo(() => {
    const queryableFields: (keyof DocumentData)[] = [
      "heading",
      "description",
      "path",
      "type",
    ]
    return createTypesenseSearchClient(
      clientKey,
      queryableFields,
      20,
      "publication_date:desc"
    )
  }, [clientKey])

  const entityFilter = `entity:${entity}${
    refinements
      ? refinements.map(
          ({ facet, value, operator }) => ` ${operator} ${facet}:${value}`
        )
      : ""
  }`

  const filterConfiguration = entityFilter

  const routing: RouterProps<UiState, RouteState> = {
    router: history({
      push(url) {
        window.history.replaceState(null, "", url)
      },
    }),
    stateMapping: {
      stateToRoute(uiState) {
        const indexUiState = uiState[collection]

        const menuUiState = Object.fromEntries(
          filters
            // @ts-expect-error - indexUiState.menu type not fully defined
            .map(({ field }) => [field, indexUiState.menu?.[field]])
            .filter(([, value]) => value !== undefined)
        )

        return {
          // @ts-expect-error - indexUiState.query type not fully defined
          q: indexUiState.query,
          ...menuUiState,
        }
      },
      routeToState(routeState) {
        const menuRouteState = Object.fromEntries(
          filters
            .map(({ field }) => [field, routeState[field]])
            .filter(([, value]) => value !== undefined)
        )

        return {
          [collection]: {
            query: routeState.q,
            menu: menuRouteState,
          },
        }
      },
    },
  }

  return (
    <InstantSearch
      searchClient={searchClient}
      indexName={collection}
      routing={routing}
    >
      <Configure filters={filterConfiguration} />
      <div className="flex flex-wrap gap-16 w-full mb-24 tablet:mb-36 desktop:mb-48">
        <SearchBox />
        <div className="flex flex-wrap gap-16">
          {filters.map(({ name, field, labels }) => (
            <MenuSelect
              key={name}
              name={name}
              attribute={field}
              labels={labels}
            />
          ))}
          <ClearFilters />
        </div>
      </div>
      {entity === "announcement" ? <List /> : <CardGrid />}
      <Pagination />
    </InstantSearch>
  )
}
